import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  ArrowLeft,
  Send,
  Loader2,
  Phone,
  User,
  MessageSquare,
  Reply,
  X,
  SmilePlus
} from 'lucide-react';
import { dmAPI, presenceAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import notificationSound from '../utils/notificationSound';
import { LinkifyText } from '../utils/linkify';

// Available reaction emojis
const REACTION_EMOJIS = ['👍', '❤️', '🙏', '😂', '🎉', '👏'];

const DirectMessagePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [presence, setPresence] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const prevMessageCount = useRef(0);

  useEffect(() => {
    loadConversation();
    // Poll for new messages every 5 seconds
    const interval = setInterval(loadMessages, 5000);
    // Poll presence every 20 seconds
    loadPresence();
    const presenceInterval = setInterval(loadPresence, 20000);
    return () => {
      clearInterval(interval);
      clearInterval(presenceInterval);
    };
  }, [userId]);

  const loadPresence = async () => {
    try {
      const data = await presenceAPI.getUser(userId);
      setPresence(data);
    } catch (error) {
      // ignore presence errors
    }
  };

  const formatLastSeen = (isoString) => {
    if (!isoString) return 'Offline';
    const date = new Date(isoString);
    const diffMins = Math.floor((new Date() - date) / 60000);
    if (diffMins < 1) return 'Active now';
    if (diffMins < 60) return `Last seen ${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Last seen ${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `Last seen ${diffDays}d ago`;
    return `Last seen ${date.toLocaleDateString()}`;
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversation = async () => {
    try {
      const data = await dmAPI.getConversation(userId);
      setMessages(data.messages || []);
      setOtherUser(data.otherUser);
    } catch (error) {
      console.error('Error loading conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to construct proper avatar URL from various formats
  const getAvatarUrl = (avatar) => {
    if (!avatar) return null;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Handle Cloudinary URLs (new cloud storage format)
    if (avatar.includes('cloudinary.com') || avatar.includes('res.cloudinary.com')) {
      return avatar;
    }
    // Handle relative URLs (legacy local format)
    if (avatar.startsWith('/api/uploads/')) {
      return `${backendUrl}${avatar}`;
    }
    // Handle legacy absolute URLs from different environments
    if (avatar.includes('/api/uploads/')) {
      const uploadPath = avatar.substring(avatar.indexOf('/api/uploads/'));
      return `${backendUrl}${uploadPath}`;
    }
    // Fallback
    return avatar.startsWith('http') ? avatar : `${backendUrl}${avatar}`;
  };

  const loadMessages = async () => {
    try {
      const data = await dmAPI.getConversation(userId);
      const newMessages = data.messages || [];
      
      // Play sound if new messages arrived from the other person
      if (prevMessageCount.current > 0 && newMessages.length > prevMessageCount.current) {
        const newestMessage = newMessages[newMessages.length - 1];
        if (newestMessage && newestMessage.senderId !== user?.id) {
          notificationSound.playMessageSound();
        }
      }
      
      prevMessageCount.current = newMessages.length;
      setMessages(newMessages);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const replyPayload = replyTo
        ? { id: replyTo.id, userName: replyTo.userName, content: (replyTo.content || '').slice(0, 150) }
        : null;
      await dmAPI.sendMessage(userId, newMessage.trim(), replyPayload);
      setNewMessage('');
      setReplyTo(null);
      await loadMessages();
      inputRef.current?.focus();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleReaction = async (messageId, emoji) => {
    try {
      const result = await dmAPI.toggleReaction(messageId, emoji);
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, reactions: result.reactions } : m)));
    } catch (error) {
      console.error('Error toggling reaction:', error);
    }
  };

  // Resolve reactor user IDs to readable names for the "reacted by" tooltip
  const getReactorNames = (userIds = []) => {
    return userIds
      .map(id => {
        if (id === user?.id) return 'You';
        if (otherUser && id === otherUser.id) return `${otherUser.firstName} ${otherUser.lastName}`;
        return 'Someone';
      })
      .join(', ');
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = new Date(now - 86400000).toDateString() === date.toDateString();
    
    const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    
    if (isToday) {
      return `Today at ${timeStr}`;
    } else if (isYesterday) {
      return `Yesterday at ${timeStr}`;
    } else {
      return date.toLocaleDateString([], { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      }) + ` at ${timeStr}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-navy-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/messages')}
            className="text-white hover:bg-white/10"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
            {otherUser?.avatar ? (
              <img 
                src={getAvatarUrl(otherUser.avatar)} 
                alt={otherUser?.firstName}
                className="w-full h-full object-cover"
              />
            ) : (
              <User className="w-5 h-5 text-tan-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold truncate">
              {otherUser ? `${otherUser.firstName} ${otherUser.lastName}` : 'Loading...'}
            </h1>
            {presence && (presence.online || presence.lastSeen) ? (
              <p className="text-sm flex items-center gap-1.5" data-testid="dm-presence-status">
                {presence.online && <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />}
                <span className={presence.online ? 'text-green-400' : 'text-tan-400'}>
                  {presence.online ? 'Active now' : formatLastSeen(presence.lastSeen)}
                </span>
              </p>
            ) : otherUser?.phone ? (
              <p className="text-sm text-tan-400">{otherUser.phone}</p>
            ) : null}
          </div>
          {otherUser?.phone && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => window.location.href = `tel:${otherUser.phone}`}
              className="text-white hover:bg-white/10"
              data-testid="call-btn"
            >
              <Phone className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
            <MessageSquare className="w-16 h-16 mb-4 opacity-50" />
            <p className="text-lg font-medium">No messages yet</p>
            <p className="text-sm">Send a message to start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => {
            const isOwn = message.senderId === user?.id;
            return (
              <div
                key={message.id}
                className={`flex items-end gap-1 group ${isOwn ? 'justify-end flex-row-reverse' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    isOwn
                      ? 'bg-navy-900 text-white rounded-br-sm'
                      : 'bg-white text-gray-800 rounded-bl-sm shadow-md'
                  }`}
                >
                  {message.replyTo && (
                    <div
                      className={`mb-1.5 pl-2 border-l-2 rounded-r-md py-1 text-xs ${
                        isOwn ? 'border-tan-400 bg-white/10' : 'border-navy-300 bg-gray-100'
                      }`}
                      data-testid={`dm-reply-quote-${message.id}`}
                    >
                      <p className={`font-semibold ${isOwn ? 'text-tan-300' : 'text-navy-700'}`}>
                        {message.replyTo.userName}
                      </p>
                      <p className={`truncate ${isOwn ? 'text-gray-200' : 'text-gray-500'}`}>
                        {message.replyTo.content}
                      </p>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap break-words">
                    <LinkifyText className={isOwn ? "text-blue-300 hover:text-blue-200 underline" : "text-blue-600 hover:text-blue-800 underline"}>
                      {message.content}
                    </LinkifyText>
                  </p>
                  {message.reactions && Object.keys(message.reactions).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {Object.entries(message.reactions).map(([emoji, users]) => (
                        <button
                          key={emoji}
                          onClick={() => handleReaction(message.id, emoji)}
                          title={`${getReactorNames(users)} reacted with ${emoji}`}
                          className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors ${
                            users.includes(user?.id)
                              ? isOwn ? 'bg-navy-700 text-white' : 'bg-tan-200 text-tan-800'
                              : isOwn ? 'bg-navy-800 text-gray-300 hover:bg-navy-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          data-testid={`dm-reaction-${message.id}-${emoji}`}
                        >
                          <span>{emoji}</span>
                          <span>{users.length}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <p className={`text-xs mt-1 ${isOwn ? 'text-gray-300' : 'text-gray-400'}`}>
                    {formatTime(message.createdAt)}
                  </p>
                </div>
                <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        className="p-1 text-gray-400 hover:text-tan-600 transition-colors"
                        title="Add reaction"
                        data-testid={`dm-react-btn-${message.id}`}
                      >
                        <SmilePlus className="w-4 h-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-2" side={isOwn ? "left" : "right"}>
                      <div className="flex gap-1">
                        {REACTION_EMOJIS.map((emoji) => (
                          <button
                            key={emoji}
                            onClick={() => handleReaction(message.id, emoji)}
                            className="text-xl hover:scale-125 transition-transform p-1"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <button
                    onClick={() => {
                      setReplyTo({ id: message.id, userName: message.senderName, content: message.content });
                      inputRef.current?.focus();
                    }}
                    className="p-1 text-gray-400 hover:text-navy-700 transition-colors"
                    title="Reply"
                    data-testid={`dm-reply-btn-${message.id}`}
                  >
                    <Reply className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="p-4 bg-white border-t sticky bottom-0">
        {replyTo && (
          <div className="mb-2 flex items-center gap-2 bg-gray-100 rounded-xl px-3 py-2 border-l-4 border-navy-700" data-testid="dm-reply-preview">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-navy-700">Replying to {replyTo.userName}</p>
              <p className="text-xs text-gray-500 truncate">{replyTo.content}</p>
            </div>
            <button type="button" onClick={() => setReplyTo(null)} className="p-1 text-gray-400 hover:text-red-500" data-testid="dm-cancel-reply">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              // Auto-resize textarea
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={(e) => {
              // Send on Enter (without Shift)
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (newMessage.trim() && !sending) {
                  handleSendMessage(e);
                }
              }
            }}
            placeholder="Type a message..."
            className="flex-1 rounded-2xl border border-gray-200 px-4 py-3 resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
            style={{ minHeight: '48px', maxHeight: '120px' }}
            disabled={sending}
            rows={1}
            data-testid="message-input"
          />
          <Button
            type="submit"
            size="icon"
            className="bg-navy-900 hover:bg-navy-800 rounded-full w-12 h-12 flex-shrink-0"
            disabled={!newMessage.trim() || sending}
            data-testid="send-btn"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </div>
        <p className="text-xs text-gray-400 mt-1 text-center">Press Enter to send, Shift+Enter for new line</p>
      </form>
    </div>
  );
};

export default DirectMessagePage;
